import { Dispatch, SetStateAction, useEffect, useState } from "react";
import {
	DataTable,
	DataTableExpandedRows,
	DataTableRowEvent,
	DataTableValueArray,
} from "primereact/datatable";
import { Column } from "primereact/column";
import Select from "../../../components/input/Select";
import SimplePagination from "../../../components/SimplePagination";
import { CustomSortIcon } from "../../../components/DatatableHelpers";
import FormattedDate from "../../../components/FormattedDate";
import {
	PeerReviewRequestInfoClassViewModel,
	PeerReviewActivityPerWriterViewModel,
} from "../peerReviewsApiSlice";
import ReviewRequestInfoExpansionRow from "./ReviewRequestInfoExpansionRow";
import {
	actionBodyTemplate,
	draftNumberBodyTemplate,
	isAllOrEmpty,
	matchesStatus,
	progressBodyTemplate,
	reviewDateBodyTemplate,
	statusBodyTemplate,
	statusIconBodyTemplate,
	statusOptions,
	studentNameBodyTemplate,
	totalReviewsBodyTemplate,
} from "./PeerReviewActivityUtil";

export enum TableView {
	Student = "Student View",
	Class = "Class View",
}

export function ReviewRequestExpansionRow({
	data,
	writerPool,
	selectedView,
	setSelectedView,
}: {
	data: PeerReviewActivityPerWriterViewModel[];
	writerPool: {
		value: string | number;
		label: string;
	}[];
	selectedView: TableView;
	setSelectedView: Dispatch<SetStateAction<TableView>>;
}) {
	const [selectedWriter, setSelectedWriter] = useState<string>("All");
	const [selectedReviewer, setSelectedReviewer] = useState<string>("All");
	const [selectedStatus, setSelectedStatus] = useState<string>("All");
	const [expandedWriterRows, setExpandedWriterRows] = useState<
		DataTableExpandedRows | DataTableValueArray
	>();
	const [page, setPage] = useState(0);

	const matchesWriter = (writerId: string, selectedWriter: string) =>
		isAllOrEmpty(selectedWriter) || writerId === selectedWriter;

	const matchesReviewer = (reviewerId: string, selectedReviewer: string) =>
		isAllOrEmpty(selectedReviewer) || reviewerId === selectedReviewer;

	// Data
	const filteredWriters = data
		.map((writer) => {
			const writerMatches = matchesWriter(writer.writer.id.toString(), selectedWriter);

			if (!writer.requests || writer.requests.length === 0) {
				const noReviewerFilter = isAllOrEmpty(selectedReviewer);
				const noStatusFilter = isAllOrEmpty(selectedStatus);

				return writerMatches && noReviewerFilter && noStatusFilter ? writer : null;
			}

			const filteredRequests = writer.requests.filter((request) => {
				return (
					writerMatches &&
					matchesReviewer(request.reviewer.id.toString(), selectedReviewer) &&
					matchesStatus(request.reviewStatus, selectedStatus)
				);
			});

			return filteredRequests.length ? { ...writer, requests: filteredRequests } : null;
		})
		.filter((writer): writer is PeerReviewActivityPerWriterViewModel => writer !== null);

	// Views
	const tableViewOptions: TableView[] = [TableView.Student, TableView.Class];

	const getClassViewData = (): PeerReviewRequestInfoClassViewModel[] => {
		return paginatedWriters.flatMap((activity) =>
			activity.requests.map((req) => ({
				...req,
				writer: activity.writer,
				requestDate: activity.requestDate,
			}))
		);
	};

	// Pagination
	const pageSize = 10;
	const handlePageChange = (newPage: number) => {
		setPage(newPage);
		setExpandedWriterRows(undefined);
	};

	const paginatedWriters = filteredWriters.slice(page * pageSize, (page + 1) * pageSize);

	const totalCount =
		selectedView === TableView.Class ? getClassViewData().length : filteredWriters.length;

	// Reset filters and pagination when switching views.
	useEffect(() => {
		setPage(0);
		setExpandedWriterRows(undefined);
		setSelectedStatus("All");
	}, [selectedView]);

	// Student Table
	const onWriterRowToggle = (e: DataTableRowEvent) => {
		setExpandedWriterRows(e.data);
	};

	const onWriterRowClick = (e: DataTableRowEvent) => {
		const clickedRow = e.data as PeerReviewActivityPerWriterViewModel;

		if (!clickedRow.requests || clickedRow.requests.length === 0) {
			return;
		}

		const isExpanded = expandedWriterRows && (expandedWriterRows as any)[clickedRow.writer.id];

		if (isExpanded) {
			setExpandedWriterRows(undefined);
		} else {
			setExpandedWriterRows({
				[clickedRow.writer.id]: true,
			});
		}
	};

	const writerRowExpansionTemplate = (requesterData: PeerReviewActivityPerWriterViewModel) => {
		return <ReviewRequestInfoExpansionRow requesterData={requesterData} />;
	};

	const requestDateBodyTemplate = (rowData: PeerReviewActivityPerWriterViewModel) => {
		return rowData.requestDate ? <FormattedDate date={rowData.requestDate} /> : "-";
	};

	return (
		<div className="-mx-4 -my-3 p-4 bg-gray-100">
			<div className="mb-4 flex justify-between">
				<div className="flex gap-4">
					<Select
						value={selectedWriter}
						setValue={(value) => setSelectedWriter(value as string)}
						simpleListBoxId="writer-filter"
						options={writerPool}
						label="Writer"
						fieldClasses="w-64"
					/>
					<Select
						value={selectedReviewer}
						setValue={(value) => setSelectedReviewer(value as string)}
						simpleListBoxId="reviewer-filter"
						options={writerPool}
						label="Reviewer"
						fieldClasses="w-64"
					/>
					{selectedView === TableView.Class && (
						<Select
							value={selectedStatus}
							setValue={(value) => setSelectedStatus(value as string)}
							simpleListBoxId="status-filter"
							options={statusOptions}
							label="Status"
							fieldClasses="w-64"
						/>
					)}
				</div>
				<div className="flex rounded-lg border border-gray-300 bg-white shadow-sm overflow-hidden h-fit self-end cursor-pointer">
					{tableViewOptions.map((opt, index) => {
						const isActive = selectedView === opt;
						return (
							<button
								key={opt}
								onClick={() => setSelectedView(opt)}
								className={`px-3 py-2 text-xs font-medium transition-colors cursor-pointer
									${index !== 0 ? "border-l border-gray-300" : ""}
              ${isActive ? "text-gray-900 bg-gray-100" : "bg-white text-gray-500 hover:bg-gray-100"}`}>
								{opt}
							</button>
						);
					})}
				</div>
			</div>
			{selectedView === TableView.Class && (
				<DataTable
					value={getClassViewData()}
					stripedRows
					sortIcon={CustomSortIcon}
					emptyMessage="No review requests found"
					className="tbl tbl-datatable -mb-2">
					<Column
						header="Review Requested By"
						sortField="writer.formalFullName"
						body={(rowData: PeerReviewRequestInfoClassViewModel) =>
							studentNameBodyTemplate(rowData.writer)
						}
						sortable
					/>
					<Column
						header="Request Date"
						field="requestDate"
						body={requestDateBodyTemplate}
						style={{ minWidth: "120px" }}
						sortable
					/>
					<Column
						header="Draft Reviewed"
						field="reviewedDraftNumber"
						body={(rowData: PeerReviewRequestInfoClassViewModel) =>
							draftNumberBodyTemplate(rowData.reviewedDraftNumber)
						}
						sortable
					/>
					<Column
						header="Reviewer"
						sortField="reviewer.formalFullName"
						body={(rowData: PeerReviewRequestInfoClassViewModel) =>
							studentNameBodyTemplate(rowData.reviewer)
						}
						sortable
					/>
					<Column
						body={statusIconBodyTemplate}
						headerClassName="!px-0 w-4"
						className="!px-0"></Column>
					<Column
						header="Status"
						field="isReviewed"
						body={statusBodyTemplate}
						sortable
						headerClassName="writing:w-[266px] !px-1"
						className="!px-1"
					/>
					<Column
						header="Review Date"
						field="reviewDate"
						body={reviewDateBodyTemplate}
						style={{ minWidth: "120px" }}
						sortable
					/>
					<Column body={actionBodyTemplate} headerClassName="w-16" />
				</DataTable>
			)}
			{selectedView === TableView.Student && (
				<DataTable
					value={paginatedWriters}
					dataKey="writer.id"
					stripedRows
					expandedRows={expandedWriterRows}
					onRowToggle={onWriterRowToggle}
					onRowClick={onWriterRowClick}
					rowExpansionTemplate={writerRowExpansionTemplate}
					sortIcon={CustomSortIcon}
					emptyMessage="No review requests found"
					className="tbl tbl-datatable -mb-2"
					rowClassName={(data) =>
						`group ${data.requests && data.requests.length > 0 ? "cursor-pointer" : ""}`
					}>
					<Column
						expander={(data: PeerReviewActivityPerWriterViewModel) =>
							data.requests && data.requests.length > 0
						}
						style={{ width: "3rem", cursor: "inherit !important" }}
					/>
					<Column
						header="Review Requested By"
						field="writer.formalFullName"
						body={(rowData: PeerReviewRequestInfoClassViewModel) =>
							studentNameBodyTemplate(rowData.writer)
						}
						sortable
					/>
					<Column
						header="Request Date"
						field="requestDate"
						body={requestDateBodyTemplate}
						style={{ minWidth: "120px" }}
						sortable
					/>
					<Column
						header="Draft Number When Requested"
						field="draftNumber"
						body={(rowData: PeerReviewActivityPerWriterViewModel) =>
							draftNumberBodyTemplate(rowData.requestedDraftNumber)
						}
						sortable
					/>
					<Column
						header="Total Requested Reviews"
						body={totalReviewsBodyTemplate}
						sortable
						sortField="totalReviews"
					/>
					<Column
						body={(rowData: PeerReviewActivityPerWriterViewModel) =>
							progressBodyTemplate(rowData, rowData.writer.id)
						}
						header="Status"
						style={{ minWidth: "120px" }}
					/>
				</DataTable>
			)}
			{totalCount > pageSize && (
				<SimplePagination
					itemCount={totalCount}
					page={page}
					pageSize={pageSize}
					totalCount={totalCount}
					onChange={(page) => handlePageChange(page)}
					className="border-none"
				/>
			)}
		</div>
	);
}
